import type { useIntl } from 'react-intl'

import type {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'
import { showSnackbar } from '@b-stock/fe-core/bstock-react'

import type { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { showBulkAddToWatchlistToast } from './showBulkAddToWatchlistToast'
import type {
  BulkAddToWatchlistResult,
  BulkWatchlistableListing,
} from './useBulkAddToWatchlist'
import { useBulkAddToWatchlist } from './useBulkAddToWatchlist'

type FormatMessage = ReturnType<typeof useIntl>['formatMessage']
type Track = ReturnType<typeof useAnalytics>['track']
type TrackButtonClicked = ReturnType<typeof useAnalytics>['trackButtonClicked']

type BulkAddToWatchlistActionMessageIds = {
  emptySelection: string
  success: string
  partialSuccess: string
  unexpectedError: string
  // Shown instead of emptySelection when the user had a non-empty selection
  // but every item was filtered out of `listings` as ineligible (e.g. My
  // Bids' missing-closing-time filter). Omit on surfaces that never filter
  // the raw selection (ALP/Storefront) — that branch is unreachable there.
  noEligibleSelection?: string
  // Shown after a successful submit if some selected items were filtered out
  // of `listings` as ineligible. Omit for the same reason as above.
  someIneligible?: string
  // Shown whenever submit reports skippedCount > 0 (some/all selected
  // listings were already on the watchlist) — distinct from someIneligible,
  // which only covers listings filtered out of `listings` before submit ever
  // runs. Without this, an already-watchlisted selection gets zero feedback:
  // showBulkAddToWatchlistToast is silent when every listing was skipped
  // (attempted === 0), and skippedCount is otherwise only tracked as
  // analytics on the completed event.
  alreadyWatchlisted?: string
}

type BulkAddToWatchlistActionAnalytics = {
  screenName?: string
  surface: 'alp' | 'storefront' | 'my_bids'
  source?: AnalyticsSource
  entityType?: AnalyticsEntityType
}

// Shared orchestration for bulk "Add to Watchlist" across every surface that
// offers it (ALP/Storefront's BulkActionsBar, My Bids' toolbar): the
// initiated/completed/error analytics events, the success/partial-success
// toast, and the empty-selection/unexpected-error snackbars all live here so
// a schema change only has to happen once.
export function useBulkAddToWatchlistAction(
  listings: BulkWatchlistableListing[],
  {
    totalSelectedCount,
    analytics,
    messageIds,
    track,
    trackButtonClicked,
    formatMessage,
  }: {
    // Raw user-selection size, from whatever the surface's own selection
    // hook reports (e.g. useBulkSelection's `count`), before any eligibility
    // filtering narrowed it down to `listings`. Pass the selection hook's own
    // count rather than `listings.length` even when the two are not expected
    // to diverge — `listings` may be sourced from a prop that lags the
    // selection hook by a render (see ListView.tsx), so the selection hook's
    // count is the more current guard for "is anything selected".
    totalSelectedCount: number
    analytics: BulkAddToWatchlistActionAnalytics
    messageIds: BulkAddToWatchlistActionMessageIds
    // Passed in rather than derived via useAnalytics()/useIntl() here — every
    // caller already holds both for its own tracking/messages, so deriving
    // them again in this hook would just double those (non-trivial) hook
    // calls per render.
    track: Track
    // Fires the same per-listing add_to_watchlist event as the LDP button
    // and GLOB-4627's heart icon, once per listing actually added, so
    // aggregate watchlist-addition reporting stays unified across entry
    // points (see BP-2545/!2372 — this was dropped during the hook
    // extraction and restored per GLOB-4837 QA follow-up).
    trackButtonClicked: TrackButtonClicked
    formatMessage: FormatMessage
  }
) {
  const { submit: submitAddToWatchlist, isPending } =
    useBulkAddToWatchlist(listings)

  // Only surfaces that model an eligibility concept (i.e. supply one of these
  // message ids) get an ineligible_count field — otherwise every surface's
  // event picks up a field that's structurally always 0 for it.
  const hasEligibilityConcept = Boolean(
    messageIds.noEligibleSelection || messageIds.someIneligible
  )
  const ineligibleCount = totalSelectedCount - listings.length

  const handleClick = async () => {
    if (totalSelectedCount === 0) {
      showSnackbar({
        content: formatMessage({ id: messageIds.emptySelection }),
        options: { type: 'error', autoClose: 'long' },
      })
      return
    }

    if (messageIds.noEligibleSelection && listings.length === 0) {
      showSnackbar({
        content: formatMessage({ id: messageIds.noEligibleSelection }),
        options: { type: 'error', autoClose: 'long' },
      })
      return
    }

    // listings (not totalSelectedCount) is the set actually submitted below,
    // so selection_count/listing_ids here reconcile against the completed
    // event's added_count + skipped_count + failed_count.
    track('bulk_watchlist_add_initiated', {
      ...analytics,
      selection_count: listings.length,
      listing_ids: listings.map((listing) => listing.listingId),
      ...(hasEligibilityConcept ? { ineligible_count: ineligibleCount } : {}),
    })

    let result: BulkAddToWatchlistResult
    try {
      result = await submitAddToWatchlist()
    } catch (error) {
      track('bulk_watchlist_add_error', {
        ...analytics,
        error_type: error instanceof Error ? error.name : 'unknown_error',
        // Same basis as the initiated event above (listings.length, not
        // totalSelectedCount) so the two reconcile for the same click.
        selection_count: listings.length,
      })
      showSnackbar({
        content: formatMessage({ id: messageIds.unexpectedError }),
        options: { type: 'error', autoClose: 'long' },
      })
      return
    }

    track('bulk_watchlist_add_completed', {
      ...analytics,
      added_count: result.addedCount,
      skipped_count: result.skippedCount,
      failed_count: result.failedCount,
      added_listing_ids: result.addedListingIds,
    })

    // Fires alongside (not instead of) bulk_watchlist_add_completed above —
    // failed_count on that event isn't enough on its own for alerting, since
    // most dashboards key off a dedicated error event rather than a field
    // inside a completed one. Without this, a full watchlist-service outage
    // produced zero _error events despite every add failing (GLOB-4837 QA
    // follow-up). error_type distinguishes this from the catch block above,
    // which only ever fires for unexpected client-side exceptions.
    if (result.failedCount > 0) {
      track('bulk_watchlist_add_error', {
        ...analytics,
        error_type:
          result.failedCount === result.attempted
            ? 'total_failure'
            : 'partial_failure',
        selection_count: listings.length,
        failed_count: result.failedCount,
        attempted: result.attempted,
      })
    }

    result.addedListingIds.forEach((listingId) => {
      trackButtonClicked({
        buttonName: 'add_to_watchlist',
        listing_id: listingId,
        surface: analytics.surface,
      })
    })

    showBulkAddToWatchlistToast(result, formatMessage, {
      success: messageIds.success,
      partialSuccess: messageIds.partialSuccess,
    })

    if (messageIds.someIneligible && ineligibleCount > 0) {
      showSnackbar({
        content: formatMessage(
          { id: messageIds.someIneligible },
          { count: ineligibleCount }
        ),
        options: { type: 'default', autoClose: 'long' },
      })
    }

    if (messageIds.alreadyWatchlisted && result.skippedCount > 0) {
      showSnackbar({
        content: formatMessage(
          { id: messageIds.alreadyWatchlisted },
          { count: result.skippedCount }
        ),
        options: { type: 'default', autoClose: 'long' },
      })
    }
  }

  return { handleClick, isPending }
}
